import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { FilterForm, TextInput, useDelete, useNotify, useTranslate } from 'react-admin';
import { CLIENT_METADATA_AVATAR_TYPE_NAME, GROUP_USERS, SITES_ROUTE, USERS_ROUTE } from '@/utils/CONST';
import CreateAgencyDialog from '@/components/agencies/create/create-agency';
import { InfiniteListBase, WithListContext } from 'react-admin';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { FilterContext, InfinitePagination } from 'ra-ui-materialui';
import { useLocation, useNavigate, useParams } from 'react-router';
import { map, range } from 'lodash';
import AccordionItemSkeleton from '@/components/navigation/navigation-items/accordion-item-skeleton';
import { GroupUser } from '@/utils/types';
import AccordionItem from '@/components/navigation/navigation-items/accordion-item';
import { Delete, EditOutlined } from '@mui/icons-material';
import extractIdFromURI from '@/utils/id';
import DeleteDialog from '@/components/common/delete-dialog/delete-dialog';
import { List, ListItemButton } from '@mui/material';

const AgenciesContainer = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const [deleteOne, { isLoading: isDeleting }] = useDelete();
    const notify = useNotify();
    const [agencyIdToDelete, setAgencyIdToDelete] = useState<string | null>(null);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const { agencyId } = useParams();
    const location = useLocation();

    const onRowClick: any = (id: string) => {
        navigate(extractIdFromURI(id) ?? '');
    };

    const handleClick = () => setOpenCreateDialog(true);

    const goToEditAgency = (agencyId: string) => {
        navigate(`edit/${extractIdFromURI(agencyId)}`);
    };

    const handleDeleteOne = (agencyId: string) => {
        deleteOne(
            GROUP_USERS,
            {
                id: agencyId,
            },
            {
                onSuccess: () => {
                    notify('ra.notification.deleted', {
                        type: 'success',
                        messageArgs: { smart_count: 1 },
                    });
                    setAgencyIdToDelete(null);
                },
                mutationMode: 'optimistic',
            },
        );
    };

    return (
        <Box
            sx={{
                display: 'grid',
                // gridTemplateRows: permissions?.includes(ROLE_BIG_CREATE_CLIENT) ? 'auto 1fr' : '1fr',
                alignContent: 'start',
                height: '100%',
                overflowY: 'auto',
            }}
        >
            <Box p={0.5} display={'grid'} gridTemplateColumns={'1fr'}>
                <Button onClick={handleClick}>
                    <Box display={'flex'} gap={1} alignItems={'center'} fontSize={24}>
                        <PlaylistAddIcon />

                        {
                            <Typography variant={'button'} fontSize={12} fontWeight={700}>
                                {translate('agency.add_agency')}
                            </Typography>
                        }
                    </Box>
                </Button>
            </Box>

            <InfiniteListBase
                resource={GROUP_USERS}
                sort={{ order: 'DESC', field: 'createdAt' }}
                storeKey='groupUsersList'
                filter={{
                    'metadataAvatarType.name': CLIENT_METADATA_AVATAR_TYPE_NAME,
                    enabled: true,
                }}
            >
                <WithListContext
                    render={({ data, isLoading, total }) => (
                        <>
                            <Box
                                sx={{
                                    '& .RaFilterFormInput-spacer': {
                                        display: 'none',
                                    },
                                    '& form': {
                                        minHeight: 0,
                                    },
                                    '& form div': {
                                        width: '100%',
                                    },
                                    '& form .RaFilterForm-clearFix': {
                                        display: 'none',
                                    },
                                }}
                            >
                                <FilterContext.Provider
                                    value={[
                                        <TextInput
                                            name='name'
                                            placeholder={translate('agency.form.agency_name')}
                                            label={false}
                                            source='name'
                                            helperText={false}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    borderRadius: 0,
                                                },
                                                mt: '0 !important',
                                            }}
                                            alwaysOn
                                        />,
                                    ]}
                                >
                                    <FilterForm />
                                </FilterContext.Provider>
                            </Box>

                            <Box
                                sx={{
                                    height: '100%',
                                    overflowY: 'auto',
                                }}
                            >
                                {map(data, ({ id, name }: GroupUser) => (
                                    <Box key={id}>
                                        <AccordionItem
                                            defaultExpanded={agencyId === extractIdFromURI(id)}
                                            onSelect={() => onRowClick(id)}
                                            selected={agencyId === extractIdFromURI(id)}
                                            id={id}
                                            name={name}
                                            menuItems={[
                                                {
                                                    label: translate('ra.action.edit'),
                                                    icon: <EditOutlined />,
                                                    onClick: () => goToEditAgency(id),
                                                },
                                                {
                                                    label: translate('ra.action.delete'),
                                                    icon: <Delete />,
                                                    onClick: () => setAgencyIdToDelete(id),
                                                },
                                            ]}
                                        >
                                            <Box>
                                                <List disablePadding>
                                                    <ListItemButton
                                                        onClick={() => navigate(`${extractIdFromURI(id)}/${USERS_ROUTE}`)}
                                                        selected={
                                                            agencyId === extractIdFromURI(id) &&
                                                            location.pathname.includes(`/${USERS_ROUTE}`)
                                                        }
                                                    >
                                                        <Typography pl={7} fontSize={14}>
                                                            {translate('main.navigation.users')}
                                                        </Typography>
                                                    </ListItemButton>
                                                    <ListItemButton
                                                        onClick={() =>
                                                            navigate(`${extractIdFromURI(id)}/${SITES_ROUTE}`)
                                                        }
                                                        selected={
                                                            agencyId === extractIdFromURI(id) &&
                                                            location.pathname.includes(SITES_ROUTE)
                                                        }
                                                    >
                                                        <Typography pl={7} fontSize={14}>
                                                            {translate('agency.list.sites.title')}
                                                        </Typography>
                                                    </ListItemButton>
                                                </List>
                                            </Box>
                                        </AccordionItem>
                                    </Box>
                                ))}

                                {isLoading && range(3).map((index) => <AccordionItemSkeleton key={index} />)}

                                {data && total !== undefined && data.length < total && <InfinitePagination />}
                            </Box>

                            {!!agencyIdToDelete && (
                                <DeleteDialog
                                    isLoading={isDeleting}
                                    onClose={() => setAgencyIdToDelete(null)}
                                    onClick={() => handleDeleteOne(agencyIdToDelete)}
                                />
                            )}

                            {openCreateDialog && (
                                <CreateAgencyDialog
                                    open={openCreateDialog}
                                    onClose={() => setOpenCreateDialog(false)}
                                />
                            )}
                        </>
                    )}
                />
            </InfiniteListBase>
        </Box>
    );
};

export default AgenciesContainer;
